import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import type {
	TAppDispatch,
	TRootState,
} from "../../redux/store";
import type {
	TConversation,
	TMessage,
} from "../../types/agent";

import { ChevronDownIcon } from "../../assets/icons/chevronDownIcon";
import { ChevronRightIcon } from "../../assets/icons/chevronRightIcon";
import { DbIcon } from "../../assets/icons/dbIcon";
import { POLLING_STATUS } from "../../constants/agent";
import {
	addModelMessage,
	addUserMessage,
	setPollingMessageId,
	syncConversationTurnCount,
} from "../../redux/agent/agent.slice";
import { services } from "../../services";
import { Loader } from "../../shared/Loader";
import { getApiErrorMessage } from "../../utils/apiErrorUtils";

import PaulThinkingIndicator from "./PaulThinkingIndicator";

const LEDGER_SQL_PROMPT =
	"give me the sql formula for ledger for this";

type TChatInputProps = {
	conversationId: string;
	selectedConversation: TConversation | null;
	maxQueries: number;
	remainingQueries: number;
	usedQueries: number;
	isUsageLoading: boolean;
	queryUsageRevealCount: number;
	scrollToBottom: () => void;
	readOnly?: boolean;
	inputLocked?: boolean;
	paulStatus?: string | null;
};

const ChatInput: React.FC<TChatInputProps> = ({
	conversationId,
	selectedConversation,
	maxQueries,
	remainingQueries,
	usedQueries,
	isUsageLoading,
	queryUsageRevealCount,
	scrollToBottom,
	readOnly = false,
	inputLocked = false,
	paulStatus,
}) => {
	const [input, setInput] = useState("");
	const [
		areQuickActionsExpanded,
		setAreQuickActionsExpanded,
	] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const activeConversationIdRef = useRef<string | null>(
		conversationId
	);
	const dispatch = useDispatch<TAppDispatch>();
	const { pollingMessageId } = useSelector(
		(state: TRootState) => state.agent
	);
	const canEdit =
		!readOnly &&
		(!selectedConversation ||
			selectedConversation.is_owner);
	const isQueryLimitReached = remainingQueries <= 0;
	const shouldShowUsage =
		usedQueries >= maxQueries - queryUsageRevealCount;
	const usageTone = isQueryLimitReached
		? "exhausted"
		: remainingQueries <= queryUsageRevealCount
			? "warning"
			: "healthy";
	const isInputDisabled =
		!canEdit || isQueryLimitReached || inputLocked;
	const isSendDisabled =
		isInputDisabled || !!pollingMessageId || isSending;

	useEffect(() => {
		setAreQuickActionsExpanded(false);
		activeConversationIdRef.current = conversationId;

		return () => {
			if (
				activeConversationIdRef.current === conversationId
			) {
				activeConversationIdRef.current = null;
			}
		};
	}, [conversationId]);

	const sendMessage = async (
		messageText: string,
		options?: { clearInput?: boolean }
	) => {
		const trimmedMessage = messageText.trim();
		if (!trimmedMessage || !selectedConversation) {
			return;
		}

		if (isQueryLimitReached) {
			toast.error(
				`This chat has reached its ${maxQueries}-query limit.`
			);
			return;
		}

		if (isSendDisabled) {
			return;
		}

		const userMessage: TMessage = {
			msg_id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			role: "user",
			turn_idx: -1,
			time_millis: Date.now(),
			content: [
				{
					msg_type: "paragraph",
					content: trimmedMessage,
					blob_link: null,
					extension: "",
					is_large_file: false,
				},
			],
			is_internal: false,
			attachments: null,
			refresh_sas_link_before_millis: null,
			error_msg: "",
		};

		setIsSending(true);
		dispatch(addUserMessage(userMessage));
		dispatch(
			setPollingMessageId(`pending-${userMessage.msg_id}`)
		);
		if (options?.clearInput) {
			setInput("");
		}
		setTimeout(scrollToBottom, 100);

		try {
			const response =
				await services.agentServiceInstance.sendMessageToConversation(
					conversationId,
					trimmedMessage,
					selectedConversation.agent_id,
					{ module: "native_paul" }
				);
			if (
				activeConversationIdRef.current !== conversationId
			) {
				return;
			}

			if (typeof response.turn_idx === "number") {
				dispatch(
					syncConversationTurnCount({
						conv_id: conversationId,
						num_turns: response.turn_idx + 1,
					})
				);
			}

			const status = response.status.status;
			if (status && status !== "Ready") {
				const modelPlaceholder: TMessage = {
					msg_id: response.msg_id,
					role: "model",
					turn_idx: response.turn_idx ?? -1,
					time_millis: Date.now(),
					content: [],
					is_internal: false,
					attachments: null,
					refresh_sas_link_before_millis: null,
					error_msg: "",
					status: POLLING_STATUS.IN_PROGRESS,
				};
				dispatch(addModelMessage(modelPlaceholder));
				dispatch(setPollingMessageId(response.msg_id));
			} else {
				dispatch(setPollingMessageId(null));
			}
		} catch (error) {
			if (
				activeConversationIdRef.current !== conversationId
			) {
				return;
			}
			const errorMsg = getApiErrorMessage(
				error,
				"Paul could not send your message. Please try again."
			);
			dispatch(setPollingMessageId(null));
			dispatch(
				addModelMessage({
					msg_id: `temp-error-${Date.now()}`,
					role: "model",
					turn_idx: userMessage.turn_idx,
					time_millis: Date.now(),
					content: [],
					is_internal: false,
					attachments: null,
					refresh_sas_link_before_millis: null,
					error_msg: errorMsg,
					status: POLLING_STATUS.ERROR,
				})
			);
			toast.error(errorMsg);
		} finally {
			if (
				activeConversationIdRef.current === conversationId
			) {
				setIsSending(false);
			}
		}
	};

	const handleSend = async () => {
		await sendMessage(input, { clearInput: true });
	};

	const handleSendLedgerPrompt = async () => {
		setAreQuickActionsExpanded(false);
		await sendMessage(LEDGER_SQL_PROMPT);
	};

	return (
		<>
			<div className="agent-input-main-container">
				<div className="agent-input-container">
					<div className="agent-input-box">
						{shouldShowUsage && (
							<div
								className={`agent-input-usage agent-input-usage--${
									isUsageLoading ? "loading" : usageTone
								}`}
							>
								<div className="agent-input-usage__copy">
									<span className="agent-input-usage__label">
										Chat query usage
									</span>
									<span className="agent-input-usage__hint">
										{isUsageLoading
											? "Checking usage for this chat..."
											: isQueryLimitReached
												? "This chat has hit its limit. Create a new chat to continue."
												: `${remainingQueries} queries left in this chat.`}
									</span>
								</div>
								<div className="agent-input-usage__stats">
									{isUsageLoading ? (
										<span className="agent-input-usage__loading">
											<Loader
												width="16"
												strokeWidth="10"
											/>
											Loading
										</span>
									) : (
										<>
											<span className="agent-input-usage__stat">
												<strong>{remainingQueries}</strong>
												<span>left</span>
											</span>
											<span className="agent-input-usage__stat">
												<strong>{usedQueries}</strong>
												<span>used</span>
											</span>
											<span className="agent-input-usage__total">
												/{maxQueries}
											</span>
										</>
									)}
								</div>
							</div>
						)}
						<div className="agent-input-row">
							{selectedConversation && canEdit && (
								<div className="agent-input-inline-actions">
									<button
										type="button"
										className={`agent-input-shortcuts-toggle ${
											areQuickActionsExpanded
												? "expanded"
												: ""
										}`}
										onClick={() => {
											setAreQuickActionsExpanded(
												(currentState) => !currentState
											);
										}}
										aria-expanded={areQuickActionsExpanded}
										aria-controls="agent-quick-actions"
									>
										<span
											className="agent-input-shortcuts-toggle-badge"
											aria-hidden="true"
										>
											<DbIcon />
										</span>
										<span className="agent-input-shortcuts-toggle-label">
											Actions
										</span>
										<span className="agent-input-shortcuts-toggle-meta">
											<span className="agent-input-shortcuts-toggle-count">
												1
											</span>
											<span
												className="agent-input-shortcuts-toggle-icon"
												aria-hidden="true"
											>
												<ChevronDownIcon />
											</span>
										</span>
									</button>
									<div
										id="agent-quick-actions"
										className={`agent-input-shortcuts-panel ${
											areQuickActionsExpanded
												? "expanded"
												: "collapsed"
										}`}
									>
										<button
											type="button"
											className="agent-input-shortcut"
											onClick={() => {
												void handleSendLedgerPrompt();
											}}
											disabled={isSendDisabled}
											title={`Send "${LEDGER_SQL_PROMPT}" to Paul`}
											tabIndex={
												areQuickActionsExpanded ? 0 : -1
											}
										>
											<span
												className="agent-input-shortcut-icon"
												aria-hidden="true"
											>
												<DbIcon />
											</span>
											<span className="agent-input-shortcut-title">
												Ledger SQL
											</span>
										</button>
									</div>
								</div>
							)}
							<textarea
								value={input}
								onChange={(e) => {
									setInput(e.target.value);
								}}
								placeholder={
									readOnly
										? "Read-only view"
										: isQueryLimitReached
											? `This chat reached the ${maxQueries}-query limit. Create a new chat to keep going.`
											: canEdit
												? inputLocked
													? "Answer Paul’s question above"
													: "Ask anything"
												: "Read-only view"
								}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !e.shiftKey) {
										e.preventDefault();
										void handleSend();
									}
								}}
								disabled={isInputDisabled}
								rows={1}
								style={{ resize: "none" }}
							/>
							<button
								onClick={handleSend}
								className="icon-button send-button"
								disabled={isSendDisabled || !input.trim()}
							>
								{Boolean(pollingMessageId) || isSending ? (
									<PaulThinkingIndicator
										variant="icon"
										status={paulStatus}
										message="Paul is working on your request"
									/>
								) : (
									<ChevronRightIcon />
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
			<div className="disclaimer">
				{isQueryLimitReached
					? `This chat has used all ${maxQueries} available queries. Create a new chat to continue.`
					: "Paul can make mistakes. Check important info."}
			</div>
		</>
	);
};

export default ChatInput;
